"use client";

import DynamicIcon from "@/components/atoms/DynamicIcon";
import { ctaClick } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { isCmsShowFlagOn } from "@/utils/variantHelpers";
import Image from "next/image";
import Link from "next/link";
import { useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { LEAD_DROP_VARIANTS } from "../../../constants";

/**
 * Program Cards 1067 — KUK "Explore other Specialisations".
 *
 * Port of kuk-campus-portal `components/Degree/OtherSpecialisations.tsx`: a two-line
 * heading in a left column beside the sibling specialisations of the programme being
 * viewed — plain text cards with a Know More and a Brochure button from 768px up, and
 * a tinted thumbnail-beside-text row below it.
 *
 * TWO LAYOUTS, ONE LOOP. v1 writes the card out twice — a compact row below `md` and a
 * taller text card above it — duplicating every field and both CTAs (and it keeps a
 * third, commented-out copy of the mobile card). They differ only in arrangement, so
 * this renders one card that changes shape at `tab`, which is half the markup and
 * cannot drift between breakpoints.
 *
 * THE THUMBNAIL IS MOBILE-ONLY, as in the reference: the desktop card is text alone.
 *
 * BREAKPOINTS — the v1 source is built on Tailwind's DEFAULT scale, where `md:` is
 * 768px. This app remaps `md:` to 1230px and exposes 768px as `tab:` (see globals.css
 * `--breakpoint-*`), so every `md:` in the reference is ported as `tab:`.
 *
 * THE DESCRIPTION CLAMP DIFFERS BY WIDTH in the reference — the desktop card prints
 * the first seven words followed by an ellipsis, the mobile row truncates at 130
 * characters and then clamps to three lines. Both are kept, because a single rule
 * would either strand the desktop card's fixed height or crowd the mobile row.
 *
 * NOT CARRIED OVER — v1's `isUserVerified` branch (download straight through vs open
 * the sign-up form) and its `window.location.origin` prefix on the Know More href,
 * which produces an absolute URL to the page it is already on. The card URL is a
 * stored field here, so it can point anywhere.
 */


/**
 * v1's `.custom-underline` — a short accent rule under the section heading, used on
 * 12 headings across the reference. Defined in ITS globals.css as a `::after` with
 * `bottom:-6px; left:0; width:33%; height:5px; background:#9f1c33`.
 *
 * Reproduced as a real element rather than a pseudo-element so the colour can come
 * from a CMS field (a pseudo-element cannot read an inline style). `w-fit` on the
 * heading is load-bearing: the rule is 33% of the HEADING, and a full-width h2 would
 * stretch it across a third of the page.
 */
const HEADING_UNDERLINE = "absolute -bottom-1.5 left-0 h-[5px] w-1/3";

/** The 14x14 info glyph the reference renders beside "Know more" below 768px. */
const InfoIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
    className="shrink-0"
  >
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
    <path
      d="M12 16v-4M12 8h.01"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

interface CardRow {
  id?: string | number;
  /** Parent programme, the card's bold first line. */
  meta_parent_title?: string;
  /** Specialisation name. */
  meta_title?: string;
  meta_description?: string;
  meta_duration?: string;
  meta_image?: string;
  meta_alt_image?: string;
  meta_url?: string;
  /** Brochure URL; used only when the Brochure CTA is set to link. */
  meta_brochure_url?: string;
  meta_priority?: number;
  meta_status?: boolean;
}

interface ProgramCards1067Data {
  metadata?: CardRow[];
  /** Eyebrow line above the heading. */
  pre_title?: string;
  pre_title_color?: string;
  /** Heading; `title_highlight_color` paints its last word. */
  title?: string;
  title_text_color?: string;
  title_highlight_color?: string;
  desktop_background_color?: string;
  /** Desktop card surface. */
  card_background_color?: string;
  /** Mobile row surface. */
  mobile_background_color?: string;
  /** Card copy. */
  salary_text_color?: string;
  /** Duration line. */
  note?: string;
  /** Know More CTA. */
  cta_text?: string;
  cta_text_color?: string;
  /** Brochure CTA. */
  load_more_text?: string;
  load_more_desktop_text_color?: string;
  load_more_mobile_background_color?: string;
  load_more_desktop_icon?: string;
  /** Off → link to each card's `meta_brochure_url` instead of opening the lead form. */
  show_features?: boolean;
  /** Mobile row thumbnail glyph beside the title. */
  cta_icon?: string;
  /** Accent rule under the heading (v1's `.custom-underline`). */
  gradient_color_1?: string;
  status?: boolean | number;
}

function normalizeHref(href: string): string {
  const h = (href || "").trim();
  if (!h) return "#";
  if (
    h.startsWith("http://") ||
    h.startsWith("https://") ||
    h.startsWith("/") ||
    h.startsWith("#") ||
    h.startsWith("mailto:") ||
    h.startsWith("tel:")
  ) {
    return h;
  }
  return `/${h.replace(/^\//, "")}`;
}

/** v1's `truncateText(text, 130)` on the mobile row. */
function truncate(text: string, max: number): string {
  if (!text) return "";
  return text.length > max ? `${text.slice(0, max)}...` : text;
}

/** v1's desktop clamp: the first seven words, then an ellipsis. */
function firstWords(text: string, count: number): string {
  if (!text) return "";
  const words = text.split(" ");
  return words.length > count ? `${words.slice(0, count).join(" ")}...` : text;
}

const ProgramCards1067 = (props: { data?: ProgramCards1067Data }) => {
  const { data: raw } = props;
  const sectionData = ((raw as any)?.data ?? raw) as
    | ProgramCards1067Data
    | undefined;
  const dispatch = useDispatch();

  const {
    pre_title = "Didn't find what you need?",
    pre_title_color = "#101112",
    title = "Explore other Specialisations",
    title_text_color = "#101112",
    title_highlight_color = "#9F1C33",
    desktop_background_color = "#FFFFFF",
    card_background_color = "#FFFFFF",
    mobile_background_color = "#FEE2E2",
    salary_text_color = "#4B4B4B",
    note = "Duration:",
    cta_text = "Know More",
    cta_text_color = "#47456E",
    load_more_text = "Brochure",
    load_more_desktop_text_color = "#FFFFFF",
    load_more_mobile_background_color = "#9F1C33",
    load_more_desktop_icon = "download",
    cta_icon = "graduation",
    gradient_color_1 = "#9F1C33",
  } = sectionData || {};

  const brochureIsCta = isCmsShowFlagOn(sectionData?.show_features, true);
  const status = isCmsShowFlagOn(sectionData?.status, true);

  const cards = useMemo(() => {
    const rows = sectionData?.metadata;
    if (!Array.isArray(rows)) return [] as CardRow[];
    return rows
      .filter((r) => r?.meta_status !== false && (r?.meta_title || r?.meta_parent_title))
      .sort(
        (a, b) => (Number(a?.meta_priority) || 0) - (Number(b?.meta_priority) || 0),
      );
  }, [sectionData?.metadata]);

  const handleBrochureClick = useCallback(
    (label: string) => {
      ctaClick({
        section: "program_cards_1067",
        component: "download_brochure",
        label: label || load_more_text || "brochure",
        step: "1",
        entity: "program_cards_1067",
        parentEntity: "program_cards_1067",
      });
      dispatch(handleOpenLeaddrop({ variant: LEAD_DROP_VARIANTS.DOWNLOAD }));
    },
    [load_more_text, dispatch],
  );

  if (!status || cards.length === 0) return null;

  /** v1 paints the heading's last word in the brand colour. */
  const words = title.trim().split(" ");
  const headingLead = words.slice(0, -1).join(" ");
  const headingAccent = words.length > 1 ? words[words.length - 1] : title;

  const renderBrochure = (card: CardRow, className: string, iconSize: string) => {
    if (!load_more_text) return null;
    const inner = (
      <>
        {load_more_desktop_icon ? (
          <DynamicIcon
            iconName={load_more_desktop_icon}
            width={iconSize}
            height={iconSize}
            color={load_more_desktop_text_color}
          />
        ) : null}
        <span>{load_more_text}</span>
      </>
    );
    const style = {
      backgroundColor: load_more_mobile_background_color,
      color: load_more_desktop_text_color,
    };
    return brochureIsCta || !card.meta_brochure_url ? (
      <button
        type="button"
        onClick={() => handleBrochureClick(card.meta_title || "")}
        className={className}
        style={style}
      >
        {inner}
      </button>
    ) : (
      <Link
        href={normalizeHref(card.meta_brochure_url)}
        className={className}
        style={style}
      >
        {inner}
      </Link>
    );
  };

  return (
    <section
      className={cn(
        "flex w-full flex-col items-start justify-center py-10",
        "tab:flex-row tab:pl-20 tab:pr-10",
      )}
      style={{ backgroundColor: desktop_background_color }}
    >
      <div className="space-y-5 px-5 tab:w-1/3">
        {pre_title ? (
          <p className="text-sm" style={{ color: pre_title_color }}>
            {pre_title}
          </p>
        ) : null}
        {title ? (
          <h2
            className="relative w-fit text-3xl font-bold"
            style={{ color: title_text_color }}
          >
            {headingLead ? `${headingLead} ` : ""}
            <span style={{ color: title_highlight_color }}>{headingAccent}</span>
            <span
              aria-hidden
              className={HEADING_UNDERLINE}
              style={{ backgroundColor: gradient_color_1 }}
            />
          </h2>
        ) : null}
      </div>

      {/* ── below tab: tinted thumbnail rows ──────────────────────────────── */}
      <div className="mt-10 w-full px-5 tab:hidden">
        <div className="grid gap-4">
          {cards.map((card, index) => (
            <div
              key={card.id ?? index}
              className="flex w-full min-w-[280px] items-center gap-2 rounded-md py-2 pl-2 shadow-md"
              style={{ backgroundColor: mobile_background_color }}
            >
              {card.meta_image ? (
                <Image
                  src={card.meta_image}
                  alt={card.meta_alt_image || card.meta_title || ""}
                  width={70}
                  height={120}
                  loading="lazy"
                  className="h-[100px] w-[60px] object-cover"
                />
              ) : null}
              <div className="relative flex min-h-[100px] flex-1 flex-col items-start justify-start gap-1.5 pr-2">
                <div className="flex items-center gap-1">
                  {cta_icon ? (
                    <DynamicIcon
                      iconName={cta_icon}
                      width="16"
                      height="16"
                      color={title_text_color}
                    />
                  ) : null}
                  <p
                    className="text-xs font-bold"
                    style={{ color: title_text_color }}
                  >
                    {card.meta_parent_title}{" "}
                    <span className="text-[10px] font-extralight">
                      {card.meta_title}
                    </span>
                  </p>
                </div>
                {card.meta_description ? (
                  <p
                    className="line-clamp-3 text-[11px]"
                    style={{ color: salary_text_color }}
                  >
                    {truncate(card.meta_description, 130)}
                  </p>
                ) : null}
                <div className="mt-auto flex items-center justify-between gap-x-[5%] pt-1 min-[400px]:gap-x-[35%]">
                  {cta_text ? (
                    <Link
                      href={normalizeHref(card.meta_url || "")}
                      className="flex items-center gap-0.5 text-xs font-bold underline"
                      style={{ color: title_highlight_color }}
                    >
                      <InfoIcon />
                      {cta_text}
                    </Link>
                  ) : null}
                  {renderBrochure(
                    card,
                    "flex items-center gap-0.5 bg-transparent text-xs font-bold underline",
                    "12",
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── tab and up: text cards ────────────────────────────────────────── */}
      <div className="hidden w-2/3 items-stretch justify-center gap-5 tab:flex">
        {cards.map((card, index) => (
          <div
            key={card.id ?? index}
            className="relative flex h-[221px] w-[295px] flex-col justify-start rounded-lg p-5 shadow-lg"
            style={{ backgroundColor: card_background_color }}
          >
            {card.meta_parent_title ? (
              <p
                className="text-lg font-bold"
                style={{ color: title_text_color }}
              >
                {card.meta_parent_title}
              </p>
            ) : null}
            <div className="flex flex-col gap-3 pt-2">
              {card.meta_title ? (
                <p
                  className="text-sm font-bold"
                  style={{ color: title_highlight_color }}
                >
                  {card.meta_title}
                </p>
              ) : null}
              {card.meta_description ? (
                <span
                  className="text-sm font-light"
                  style={{ color: salary_text_color }}
                >
                  {firstWords(card.meta_description, 7)}
                </span>
              ) : null}
              {card.meta_duration ? (
                <span className="text-sm" style={{ color: salary_text_color }}>
                  {note} {card.meta_duration}
                </span>
              ) : null}
            </div>
            <div className="absolute inset-x-5 bottom-0 flex justify-start gap-4 py-5">
              {cta_text ? (
                <Link
                  href={normalizeHref(card.meta_url || "")}
                  className="flex h-[40px] w-[120px] items-center justify-center rounded-md border text-sm font-bold"
                  style={{ color: cta_text_color, borderColor: cta_text_color }}
                >
                  {cta_text}
                </Link>
              ) : null}
              {renderBrochure(
                card,
                "flex h-[40px] w-[120px] items-center justify-center gap-2 rounded-md text-sm font-bold",
                "14",
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default ProgramCards1067;
